// panels/AudioPanel.jsx
import React, { useEffect, useRef, useState } from "react";
import { PlusCircle, Trash2, Music, Link2, Upload } from "lucide-react";

const LIBRARY_TRACKS = [
  { id: "lib-chill", label: "Chill Beat", src: "/audio/chill.mp3" },
  { id: "lib-soft", label: "Soft Piano", src: "/audio/soft-piano.mp3" },
];

export default function AudioPanel({ audioTracks, setAudioTracks }) {
  const fileInputRef = useRef(null);
  const [urlInput, setUrlInput] = useState("");
  const [startAtInput, setStartAtInput] = useState(0);

  const addTrack = (base) => {
    const track = {
      id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      startAt: Number(startAtInput) || 0,
      volume: 1,
      muted: false,
      loop: false,
      ...base,
    };
    setAudioTracks((prev) => [...prev, track]);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach((file) => {
      const url = URL.createObjectURL(file);
      addTrack({
        type: "file",
        src: url,
        label: file.name,
      file: file,   // 🔥 IMPORTANT 🔥
      });
    });
    e.target.value = "";
  };

  const handleAddUrl = () => {
    if (!urlInput.trim()) return;
    addTrack({
      type: "url",
      src: urlInput.trim(),
      label: urlInput.trim(),
    });
    setUrlInput("");
  };

  const handleAddLibraryTrack = (lib) => {
    addTrack({
      type: "library",
      src: lib.src,
      label: lib.label,
    });
  };

  const updateTrack = (id, patch) => {
    setAudioTracks((prev) =>
      prev.map((t) => (t.id === id ? { ...t, ...patch } : t))
    );
  };

  const removeTrack = (id) => {
    setAudioTracks((prev) => prev.filter((t) => t.id !== id));
  };

  useEffect(() => {
    console.log("Audio tracks updated:", audioTracks);
  }, [audioTracks]);

  return (
  <div
    className="
      p-4 flex flex-col gap-4
      text-gray-900 dark:text-gray-100
    "
  >
    {/* Title */}
    <h3 className="text-lg font-semibold flex items-center gap-2">
      <Music size={18} />
      Audio Tracks
    </h3>

    {/* ───────── Default Start Offset ───────── */}
    <div className="text-sm">
      <label className="block mb-1 text-gray-600 dark:text-gray-400">
        Default Start Time (seconds)
      </label>
      <input
        type="number"
        min="0"
        step="0.1"
        value={startAtInput}
        onChange={(e) => setStartAtInput(e.target.value)}
        className="
          w-full p-2 rounded-md
          bg-white dark:bg-gray-800
          border border-gray-300 dark:border-gray-700
          text-gray-900 dark:text-gray-100
          focus:outline-none focus:ring-2 focus:ring-blue-500
        "
      />
    </div>

    {/* ───────── Upload File ───────── */}
    <button
      onClick={() => fileInputRef.current?.click()}
      className="
        w-full flex items-center justify-center gap-2
        py-2 rounded-lg
        bg-gray-200 hover:bg-gray-300
        dark:bg-gray-700 dark:hover:bg-gray-600
        border border-gray-300 dark:border-gray-700
        transition-colors
      "
    >
      <Upload size={16} />
      Upload audio file
    </button>

    <input
      ref={fileInputRef}
      type="file"
      accept="audio/*"
      multiple
      className="hidden"
      onChange={handleFileChange}
    />

    {/* ───────── Add from URL ───────── */}
    <div className="text-sm space-y-1">
      <label className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
        <Link2 size={14} />
        Add from URL
      </label>

      <div className="flex gap-2">
        <input
          type="text"
          placeholder="https://example.com/track.mp3"
          value={urlInput}
          onChange={(e) => setUrlInput(e.target.value)}
          className="
            flex-1 p-2 rounded-md text-xs
            bg-white dark:bg-gray-800
            border border-gray-300 dark:border-gray-700
            text-gray-900 dark:text-gray-100
            placeholder:text-gray-400 dark:placeholder:text-gray-500
          "
        />

        <button
          onClick={handleAddUrl}
          className="
            px-3 py-2 rounded-md text-xs
            bg-blue-600 hover:bg-blue-700
            text-white flex items-center gap-1
          "
        >
          <PlusCircle size={14} />
          Add
        </button>
      </div>
    </div>

    {/* ───────── Free Music Library ───────── */}
    <div className="text-sm">
      <label className="block mb-1 text-gray-600 dark:text-gray-400">
        Free music
      </label>

      <div className="flex flex-wrap gap-2">
        {LIBRARY_TRACKS.map((lib) => (
          <button
            key={lib.id}
            onClick={() => handleAddLibraryTrack(lib)}
            className="
              px-2 py-1 rounded text-xs
              bg-gray-200 hover:bg-gray-300
              dark:bg-gray-700 dark:hover:bg-gray-600
              border border-gray-300 dark:border-gray-700
              transition-colors
            "
          >
            {lib.label}
          </button>
        ))}
      </div>
    </div>

    {/* ───────── Track List ───────── */}
    {audioTracks.length > 0 && (
      <div
        className="
          mt-2 pt-3 space-y-2
          max-h-52 overflow-y-auto
          border-t border-gray-200 dark:border-gray-800
        "
      >
        {audioTracks.map((track) => (
          <div
            key={track.id}
            className="
              flex flex-col gap-2 p-2 rounded-md text-xs
              bg-white dark:bg-gray-900
              border border-gray-300 dark:border-gray-700
              transition-colors
            "
          >
            {/* Header */}
            <div className="flex justify-between items-center">
              <span className="truncate max-w-[140px]">
                {track.label}
              </span>
              <button
                onClick={() => removeTrack(track.id)}
                className="text-red-500 hover:text-red-600"
              >
                <Trash2 size={14} />
              </button>
            </div>

            {/* Start time */}
            <div className="flex items-center gap-2">
              <span className="text-gray-600 dark:text-gray-400">Start</span>
              <input
                type="number"
                min="0"
                step="0.1"
                value={track.startAt}
                onChange={(e) =>
                  updateTrack(track.id, {
                    startAt: parseFloat(e.target.value) || 0,
                  })
                }
                className="
                  w-16 p-1 rounded
                  bg-white dark:bg-gray-800
                  border border-gray-300 dark:border-gray-700
                  text-gray-900 dark:text-gray-100
                "
              />
              <span className="text-gray-600 dark:text-gray-400">s</span>
            </div>

            {/* Volume */}
            <div className="flex items-center gap-2">
              <span className="text-gray-600 dark:text-gray-400">Vol</span>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={track.volume}
                onChange={(e) =>
                  updateTrack(track.id, {
                    volume: parseFloat(e.target.value),
                  })
                }
                className="flex-1 accent-blue-500"
              />
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

}
